import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import { useSnackbar } from 'notistack';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';

import useGetCapabilities from '~/hooks/apis/accommodation/useGetCapabilities';
import useGetCapability from '~/hooks/apis/accommodation/useGetCapability';
import useToggleCapability from '~/hooks/apis/accommodation/useToggleCapability';

import { PropertyResponse, listProperties } from '~/services/AccommodationService';

interface PropertyOption {
  id: string;
  name: string;
}

const SEARCH_LIMIT = 25;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function getColumns(onRemove: (property: PropertyOption) => void): Array<GridColDef<PropertyOption>> {
  return [
    { field: 'name', headerName: 'Property Name', flex: 1, minWidth: 220 },
    { field: 'id', headerName: 'Property ID', flex: 1, minWidth: 280 },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: ({ row }) => [
        <GridActionsCellItem
          key="remove"
          icon={<DeleteOutlineIcon />}
          label="Remove"
          onClick={() => onRemove(row)}
          showInMenu={false}
        />,
      ],
    },
  ];
}

export default function AccommodationFeatureRolloutContainer() {
  const [selectedFlag, setSelectedFlag] = useState('');
  const [propertyToRemove, setPropertyToRemove] = useState<PropertyOption | null>(null);
  const [selectedOption, setSelectedOption] = useState<PropertyOption | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { enqueueSnackbar } = useSnackbar();
  const flagsQuery = useGetCapabilities();
  const flags = flagsQuery.data ?? [];
  // Falls back to the first flag from the API until a tab has been explicitly clicked (or once
  // the initially-selected one no longer exists in the list).
  const currentFlag = flags.some((flag) => flag.flag === selectedFlag) ? selectedFlag : (flags[0]?.flag ?? '');

  const capabilityQuery = useGetCapability(currentFlag);
  const toggleMutation = useToggleCapability();

  const selectedFlagConfig = flags.find((flag) => flag.flag === currentFlag);

  const debounceSearch = useMemo(() => debounce((next: string) => setDebouncedSearch(next), 300), []);
  useEffect(() => () => debounceSearch.cancel(), [debounceSearch]);

  const isUuidSearch = UUID_RE.test(debouncedSearch.trim());

  const searchQuery = useQuery({
    queryKey: ['feature-rollout-property-picker-search', debouncedSearch],
    queryFn: async () => {
      const trimmed = debouncedSearch.trim();
      if (isUuidSearch) {
        const res = await listProperties({ id: trimmed, limit: '1', page: '1' });
        return res.result.properties;
      }
      if (trimmed.length < 3) {
        return [] as Array<PropertyResponse>;
      }
      const res = await listProperties({ name: trimmed, limit: String(SEARCH_LIMIT), page: '1' });
      return res.result.properties;
    },
    enabled: isUuidSearch || debouncedSearch.trim().length >= 3,
    staleTime: 30_000,
  });

  const searchOptions: Array<PropertyOption> = useMemo(
    () => (searchQuery.data ?? []).map((p) => ({ id: p.id, name: p.name })),
    [searchQuery.data],
  );

  const subscribedProperties = useMemo(() => capabilityQuery.data?.properties ?? [], [capabilityQuery.data]);

  const handleFlagChange = (_event: SyntheticEvent, next: string) => {
    setSelectedFlag(next);
    setSelectedOption(null);
    setSearchInput('');
    setDebouncedSearch('');
  };

  const resetPicker = () => {
    setSelectedOption(null);
    setSearchInput('');
    setDebouncedSearch('');
  };

  const handleAdd = async () => {
    if (!selectedOption || !currentFlag) {
      return;
    }
    const property = selectedOption;
    try {
      await toggleMutation.mutateAsync({ flag: currentFlag, propertyId: property.id, subscribe: true });
      enqueueSnackbar(`Added ${property.name} to ${selectedFlagConfig?.label ?? currentFlag}`, {
        variant: 'success',
      });
      resetPicker();
    } catch {
      // Error snackbar shown by useToggleCapability.
    }
  };

  const handleRemoveConfirm = async () => {
    if (!propertyToRemove || !currentFlag) {
      return;
    }
    const property = propertyToRemove;
    try {
      await toggleMutation.mutateAsync({ flag: currentFlag, propertyId: property.id, subscribe: false });
      enqueueSnackbar(`Removed ${property.name} from ${selectedFlagConfig?.label ?? currentFlag}`, {
        variant: 'success',
      });
      setPropertyToRemove(null);
    } catch {
      // Error snackbar shown by useToggleCapability.
    }
  };

  const columns = useMemo(() => getColumns((property) => setPropertyToRemove(property)), []);

  if (flagsQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (flagsQuery.isError || flags.length === 0) {
    return (
      <Alert severity="error">
        {flagsQuery.error instanceof Error ? flagsQuery.error.message : 'Failed to load capability flags.'}
      </Alert>
    );
  }

  return (
    <Box>
      <Tabs value={currentFlag} onChange={handleFlagChange} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {flags.map((flag) => (
          <Tab
            key={flag.flag}
            value={flag.flag}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{flag.label}</span>
                <Tooltip title={flag.description}>
                  <InfoOutlinedIcon fontSize="small" color="action" aria-label={`${flag.label} info`} />
                </Tooltip>
              </Stack>
            }
          />
        ))}
      </Tabs>

      <Alert severity="info" sx={{ mb: 2 }}>
        {selectedFlagConfig?.description}
      </Alert>

      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 360 }}>
          <Autocomplete
            options={searchOptions}
            value={selectedOption}
            onChange={(_event, next) => setSelectedOption(next)}
            inputValue={searchInput}
            onInputChange={(_event, next, reason) => {
              if (reason === 'reset') {
                return;
              }
              setSearchInput(next);
              debounceSearch(next);
            }}
            getOptionLabel={(opt) => opt.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(opts) => opts}
            loading={searchQuery.isFetching}
            noOptionsText={
              debouncedSearch.trim().length < 3 && !isUuidSearch
                ? 'Type at least 3 characters to search by name, or paste a property UUID'
                : searchQuery.isFetching
                  ? 'Searching…'
                  : 'No matching properties'
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.id}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add property"
                placeholder="Search by name…"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchQuery.isFetching ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!selectedOption || toggleMutation.isPending}
          sx={{ mt: 1 }}
        >
          Add
        </Button>
      </Stack>

      {capabilityQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {capabilityQuery.error instanceof Error
            ? capabilityQuery.error.message
            : `Failed to load properties for ${selectedFlagConfig?.label ?? currentFlag}`}
        </Alert>
      )}

      <DataGrid
        rows={subscribedProperties}
        columns={columns}
        loading={capabilityQuery.isFetching || toggleMutation.isPending}
        autoHeight
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
      />

      <Dialog
        open={!!propertyToRemove}
        onClose={toggleMutation.isPending ? undefined : () => setPropertyToRemove(null)}
        disableEscapeKeyDown={toggleMutation.isPending}
      >
        <DialogTitle>Remove property from {selectedFlagConfig?.label ?? currentFlag}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {propertyToRemove?.name} will no longer be subscribed to {selectedFlagConfig?.label ?? currentFlag}.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setPropertyToRemove(null)} disabled={toggleMutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleRemoveConfirm} disabled={toggleMutation.isPending}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
